/**
 * Props for TranslateButton component
 */
interface TranslateButtonProps {
  /** Handler called when button is clicked */
  onClick: () => void;
  /** True when translation API call is in progress */
  isTranslating: boolean;
  /** True when currently showing translated content */
  isTranslated?: boolean;
  /** Optional custom CSS classes */
  className?: string;
}

/**
 * TranslateButton - Reusable translation toggle button
 * 
 * Displays appropriate text based on translation state:
 * - "Translate this section" - Initial state
 * - "Translating..." - During API call
 * - "Show original" - When translated
 * 
 * @example
 * ```tsx
 * <TranslateButton
 *   onClick={handleToggle}
 *   isTranslating={loading}
 *   isTranslated={showingTranslation}
 * />
 * ```
 */
const TranslateButton = ({ 
  onClick, 
  isTranslating, 
  isTranslated = false,
  className = '',
}: TranslateButtonProps) => {
  return (
    <button
      onClick={onClick}
      disabled={isTranslating}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${className}`}
      aria-label={isTranslated ? "Show original content" : "Translate this section"}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" 
        />
      </svg>
      <span>
        {isTranslating ? 'Translating...' : isTranslated ? 'Show original' : 'Translate this section'}
      </span>
    </button>
  );
};

export default TranslateButton;
